/**
 * HeaderManager
 * Manages the mode-based header display with dynamic content
 *
 * Header structure:
 * - Left section: menu icon, then EITHER the plain mode label OR the mode block.
 *   Exactly one is visible at a time; both render as a plain title.
 * - Middle section: a single row of dynamic text, uniform across every mode.
 * - Right section: remaining-work control, close button, options menu, upgrade.
 * - Progress strip: a 4px bar inside .main's rounded top corners, carrying the
 *   discovery path percentage. It is the only element this class writes that
 *   lives outside the header — the header is nowrap on desktop, so a full-width
 *   child could not go there.
 *
 * Writer ownership — the load-bearing invariant of this component:
 *
 *   #header-mode-label    ← setActiveState / handleModeRestored → setModeLabel
 *   #header-mode-block    ← the active strategy → setLeftBlock
 *   #header-remaining     ← the active strategy → setLeftBlock
 *   #chat-progress-strip  ← the active strategy → setLeftBlock
 *   #header-dynamic-text  ← the active strategy → setMiddleBlock
 *
 * No element has two writers. `_renderLeftBlock` decides which of the two left
 * elements is visible but never writes the label's text — that stays with
 * setModeLabel, whose mode:changed / mode:restored path is synchronous and
 * independent of any network-backed strategy generation. (An earlier design
 * routed every mode's label through the strategy; it made the label hostage to
 * the ADVICE descriptor fetch and let a generation error blank it.)
 */

import { Tooltip } from '../utils/Tooltip.js';

export class HeaderManager {
  /** Matches the CSS opacity transition on both left elements and the middle row. */
  static FADE_MS = 150;

  /**
   * @param {HTMLElement} headerElement - The header DOM element
   * @param {ModeOrchestrator} modeOrchestrator - Reference to mode orchestrator
   * @param {EventBus} eventBus - Event bus for communication
   */
  constructor(headerElement, modeOrchestrator, eventBus) {
    this.headerElement = headerElement;
    this.modeOrchestrator = modeOrchestrator;
    this.eventBus = eventBus;

    // State
    this.currentMode = null;

    // DOM references (will be set after initialization)
    this.menuButton = null;
    this.logoElement = null;
    this.contentContainer = null;
    this.modeLabelElement = null;
    this.middleSection = null;
    this.dynamicTextElement = null;

    // Mode-block DOM refs. Deliberately NOT part of initialize()'s required-
    // element guard: a stale cached index.html should degrade to the plain
    // label, not black out the whole header.
    this.modeBlockElement = null;
    this.modeTitleElement = null;
    this.remainingElement = null;
    this.remainingTextElement = null;

    // The progress strip is the one element this class writes that lives
    // OUTSIDE the header — it is pinned inside .main's rounded top corners.
    // Same optional-by-design contract as the block refs above.
    this.chatStripElement = null;

    // Block payload state (drives change detection + fade-on-title-change)
    this._leftBlock = null;
    this._middleHtml = '';

    // Bind methods
    this.handleModeChanged = this.handleModeChanged.bind(this);
    this.handleModeRestored = this.handleModeRestored.bind(this);
    this.handleMenuButtonClick = this.handleMenuButtonClick.bind(this);
    this.updateMenuButtonBadge = this.updateMenuButtonBadge.bind(this);
    this.handleScrollIntent = this.handleScrollIntent.bind(this);
    this.handleViewportResize = this.handleViewportResize.bind(this);
  }

  /**
   * Initialize the header manager
   */
  initialize() {
    // Get DOM references
    this.logoElement = this.headerElement.querySelector('#header-logo');
    this.contentContainer = this.headerElement.querySelector('.header__content');
    this.modeLabelElement = this.headerElement.querySelector('#header-mode-label');
    this.middleSection = this.headerElement.querySelector('.header__middle');
    this.dynamicTextElement = this.headerElement.querySelector('#header-dynamic-text');
    this.menuButton = this.headerElement.querySelector('#header-menu-btn');

    // Mode-block refs — optional by design (see constructor).
    this.modeBlockElement = this.headerElement.querySelector('#header-mode-block');
    this.modeTitleElement = this.headerElement.querySelector('#header-mode-title');
    this.remainingElement = this.headerElement.querySelector('#header-remaining');
    this.remainingTextElement = this.headerElement.querySelector('#header-remaining-text');

    // Queried from the document, not the header — see constructor.
    this.chatStripElement = document.getElementById('chat-progress-strip');

    if (!this.contentContainer || !this.modeLabelElement || !this.dynamicTextElement) {
      console.error('[HeaderManager] Required DOM elements not found');
      return;
    }

    // Attach menu button click listener (toggles sidebar)
    if (this.menuButton) {
      this.menuButton.addEventListener('click', this.handleMenuButtonClick);
    }

    // Listen to mode changes and restorations (for header-specific UI like logo, dynamic text)
    this.eventBus.on('mode:changed', this.handleModeChanged);
    this.eventBus.on('mode:restored', this.handleModeRestored);

    // Roll up sidebar badge state onto the menu button (static markup, so a
    // single bind here — unlike DashboardManager's rebuilt-per-render button —
    // survives for the header's lifetime).
    this.eventBus.on('badge:state-changed', this.updateMenuButtonBadge);
    this.updateMenuButtonBadge();

    // Touch-derived scroll intent from MessageList collapses/restores the
    // middle row on phones (CSS-scoped to <760px; the class is inert wider).
    this.eventBus.on('chat:scroll-intent', this.handleScrollIntent);

    // The collapse CSS only exists below 760px, but the intent tracker toggles
    // the class at any width — clear it on layout-width changes so rotating
    // away and back never returns the user to a row collapsed by a gesture
    // from before the rotation.
    window.addEventListener('resize', this.handleViewportResize);
    window.addEventListener('orientationchange', this.handleViewportResize);

    // Initialize tooltips for icon buttons
    this.initializeTooltips();

    // Initialize with current mode if orchestrator is already set up
    if (this.modeOrchestrator?.currentMode) {
      this.handleModeChanged({ mode: this.modeOrchestrator.currentMode });
    } else {
      // Default to dashboard state (logo visible, mode selector hidden)
      this.setDashboardState();
    }

    console.log('[HeaderManager] Initialized');
  }

  /**
   * Clean up event listeners
   */
  destroy() {
    // Remove menu button listener
    if (this.menuButton) {
      this.menuButton.removeEventListener('click', this.handleMenuButtonClick);
    }

    // Remove event bus listeners
    this.eventBus.off('mode:changed', this.handleModeChanged);
    this.eventBus.off('mode:restored', this.handleModeRestored);
    this.eventBus.off('badge:state-changed', this.updateMenuButtonBadge);
    this.eventBus.off('chat:scroll-intent', this.handleScrollIntent);

    window.removeEventListener('resize', this.handleViewportResize);
    window.removeEventListener('orientationchange', this.handleViewportResize);

    console.log('[HeaderManager] Destroyed');
  }

  /**
   * Handle mode change event
   * @param {Object} data - Event data
   * @param {BaseMode} data.mode - New mode instance
   */
  async handleModeChanged(data) {
    const newMode = data.mode;

    if (!newMode) {
      console.warn('[HeaderManager] Mode change event without mode data');
      return;
    }

    this.currentMode = newMode;
    this._revealMiddleRow();
    this.setActiveState(newMode.name, newMode.getModeLabel ? newMode.getModeLabel() : '');
  }

  /**
   * Handle mode restored event (when returning from dashboard to existing session).
   * Unlike mode:changed, this payload carries the mode NAME (a string), not a
   * BaseMode instance — resolve it via modeOrchestrator.modes before reading
   * getModeLabel().
   *
   * This runs after ModeOrchestrator.restoreMode() has awaited mode.onRestore(),
   * so subMode / variant / entryType are populated by the time getModeLabel()
   * is read. That ordering is load-bearing; do not reorder restoreMode().
   *
   * @param {Object} data - Event data
   * @param {string} data.mode - Mode name
   */
  handleModeRestored(data) {
    const modeName = data.mode;

    this._revealMiddleRow();

    if (!modeName) {
      this.setDashboardState();
    } else {
      const modeInstance = this.modeOrchestrator?.modes?.get(modeName);
      const label = modeInstance?.getModeLabel ? modeInstance.getModeLabel() : '';
      this.setActiveState(modeName, label);
    }

    console.log(`[HeaderManager] Mode restored: ${modeName}`);
  }

  /**
   * Set header to dashboard state (no active mode).
   * Mode label cleared, mode block torn down, dynamic text visible.
   */
  setDashboardState() {
    // Show dynamic text container
    if (this.dynamicTextElement) {
      this.dynamicTextElement.classList.remove('header__dynamic-text--hidden');
    }

    // Drop any mode-scoped class — dashboard has no active mode.
    this._applyModeClass(null);

    // Clear both left-hand surfaces so a stale mode's identity doesn't persist
    // when the user returns to the dashboard. setLeftBlock(null) restores the
    // plain span; setModeLabel('') empties it.
    this.setLeftBlock(null);
    this.setModeLabel('');

    console.log('[HeaderManager] Set to dashboard state');
  }

  /**
   * Apply a `header--mode-<name>` modifier on the outer .header element,
   * removing any previously-applied mode class. Pass `null` to clear.
   * @param {string|null} modeName
   * @private
   */
  _applyModeClass(modeName) {
    if (!this.headerElement) return;
    // Strip any stale `header--mode-*` modifiers before adding the new one.
    Array.from(this.headerElement.classList)
      .filter(c => c.startsWith('header--mode-'))
      .forEach(c => this.headerElement.classList.remove(c));
    if (modeName) {
      this.headerElement.classList.add(`header--mode-${String(modeName).toLowerCase()}`);
    }
  }

  /**
   * Set header to active mode state
   * @param {string} modeName - Mode name (e.g., 'DISCOVERY', 'ADVICE')
   * @param {string} [label] - Static mode label text (from mode.getModeLabel())
   */
  setActiveState(modeName, label = '') {
    // Show dynamic text container
    if (this.dynamicTextElement) {
      this.dynamicTextElement.classList.remove('header__dynamic-text--hidden');
    }

    // Mode-scoped class on the outer header (e.g. `header--mode-orientation`)
    // so CSS can target mode-specific styling for any descendant.
    this._applyModeClass(modeName);

    this.setModeLabel(label);
    // Note: the mode block and dynamic text are managed by HeaderContentService,
    // not cleared here. Modes that emit a leftBlock (DISCOVERY, Foundation
    // INTAKE) hide this label entirely; the write above is harmless for them.

    console.log(`[HeaderManager] Set to active state: ${modeName}`);
  }

  /**
   * Handle menu button click (toggles sidebar)
   */
  handleMenuButtonClick() {
    // Dismiss any visible tooltip so it doesn't show stale text
    Tooltip.removeAll();

    // Toggle sidebar via event bus (Sidebar listens for this)
    if (window.eventBus) {
      window.eventBus.emit('sidebar:toggle');
    }
  }

  /**
   * Apply the rolled-up sidebar badge indicator (highest priority across all
   * sections) as a single dot on the menu button, so a collapsed sidebar's
   * notifications are still visible before opening it.
   */
  updateMenuButtonBadge() {
    if (!this.menuButton) return;

    const existingDot = this.menuButton.querySelector('.notification-dot');
    if (existingDot) existingDot.remove();

    const indicator = window.badgeService?.getAggregateIndicator?.();
    if (!indicator) return;

    const dot = document.createElement('span');
    dot.className = `notification-dot notification-dot--${indicator.color}`;
    dot.setAttribute('aria-hidden', 'true');
    this.menuButton.appendChild(dot);
  }

  /**
   * Set the static mode label text with fade animation.
   * Sole writer of #header-mode-label — see the ownership table in the file header.
   * @param {string} text - Label text (e.g. "Experiment Report"), or '' to clear
   */
  setModeLabel(text) {
    const newText = text || '';

    // Only animate if text is actually changing
    if (this.modeLabelElement.textContent === newText) {
      return;
    }

    // Fade out
    this.modeLabelElement.classList.add('header__mode-label--fade');

    // Wait for fade out, then update text and fade in
    setTimeout(() => {
      this.modeLabelElement.textContent = newText;
      this.modeLabelElement.classList.remove('header__mode-label--fade');
    }, HeaderManager.FADE_MS);
  }

  // ===== LEFT BLOCK =====

  /**
   * Public API: set or clear the rich mode block.
   *
   * Passing `null` (or a payload with no usable title) reverts the left section
   * to the plain `#header-mode-label` span, whose text is owned by setModeLabel.
   * That fallback is why plain modes — which reach here via the legacy `{ text }`
   * strategy shim — keep their labels.
   *
   * Payload shape:
   *   {
   *     title:      string,   // required, non-empty; rendered via textContent
   *     rightText?: string,   // remaining-work control; hidden when empty
   *     percent?:   number    // 0-100; the chat strip renders iff numeric
   *   }
   *
   * @param {Object|null} payload
   */
  setLeftBlock(payload) {
    const next = this._normalizeLeftBlock(payload);
    if (this._leftBlockEqual(this._leftBlock, next)) return;

    const prev = this._leftBlock;
    this._leftBlock = next;
    this._renderLeftBlock(prev, next);
  }

  /** Clear the mode block, reverting to the plain label. */
  clearLeftBlock() {
    this.setLeftBlock(null);
  }

  /**
   * Coerce a payload into the canonical shape, or null when unusable.
   * An empty title resolves to null — TreatmentMode/BaseMode both
   * return '' from getModeLabel() for unknown variants, and a block with no
   * identity would leave the left section blank.
   * @private
   */
  _normalizeLeftBlock(payload) {
    if (!payload || typeof payload !== 'object') return null;
    const title = typeof payload.title === 'string' ? payload.title.trim() : '';
    if (!title) return null;

    const percent = typeof payload.percent === 'number' && !Number.isNaN(payload.percent)
      ? Math.max(0, Math.min(100, payload.percent))
      : null;

    return {
      title,
      rightText: typeof payload.rightText === 'string' ? payload.rightText : '',
      percent
    };
  }

  /**
   * Swap between the plain label and the mode block, and write the block's
   * contents. Visibility is toggled unconditionally — it must not sit behind
   * setModeLabel's unchanged-text early return, or a mode whose label happens
   * to match the previous one would keep the wrong element hidden.
   *
   * @param {Object|null} prev - Previous normalized payload
   * @param {Object|null} next - New normalized payload
   * @private
   */
  _renderLeftBlock(prev, next) {
    const showBlock = next !== null;
    const wasBlock = prev !== null;

    // The strip lives outside the header entirely, so it does not participate in
    // the block's fade. Track the value immediately — its own width transition
    // carries the animation.
    //
    // On a title change (path → path, or the "Discovery" placeholder → the real
    // name) clear it first: the fill transitions `width`, so writing a lower
    // percent for the new path would animate the bar visibly backwards across
    // the chat. Clearing drops the markup, so the next write rebuilds at the new
    // width with no reverse sweep.
    if (showBlock && wasBlock && prev.title !== next.title) {
      this._writeLinearBar(this.chatStripElement, null);
    }
    this._writeLinearBar(this.chatStripElement, showBlock ? next.percent : null);

    // Hide the outgoing element immediately so the two never overlap.
    if (showBlock !== wasBlock) {
      if (showBlock) {
        if (this.modeLabelElement) this.modeLabelElement.hidden = true;
      } else if (this.modeBlockElement) {
        this.modeBlockElement.hidden = true;
      }
    }

    if (!showBlock) {
      // Plain path. Clear block contents so a stale title can't flash on the
      // next swap, and reveal the span (whose text setModeLabel owns).
      this._writeModeBlock(null);
      if (this.modeLabelElement) this.modeLabelElement.hidden = false;
      return;
    }

    if (!wasBlock) {
      // First appearance — nothing to fade from, so write and reveal at once.
      this._writeModeBlock(next);
      if (this.modeBlockElement) this.modeBlockElement.hidden = false;
      return;
    }

    if (prev.title === next.title) {
      // Progress-only update (the common case: one per completed section).
      // Write in place so the strip animates via its CSS transition instead of
      // flickering through an opacity swap.
      this._writeModeBlock(next);
      return;
    }

    // Title changed while visible — fade out, swap, fade in.
    this.modeBlockElement?.classList.add('header__mode-block--fade');
    setTimeout(() => {
      if (this._leftBlock !== next) return; // superseded during the fade
      this._writeModeBlock(next);
      this.modeBlockElement?.classList.remove('header__mode-block--fade');
    }, HeaderManager.FADE_MS);
  }

  /**
   * Write a normalized payload into the block's slots. Pass null to clear.
   * @private
   */
  _writeModeBlock(payload) {
    if (this.modeTitleElement) this.modeTitleElement.textContent = payload ? payload.title : '';
    this._writeRemaining(payload);
  }

  /**
   * Write the remaining-work control in the header's right section. Hidden when
   * there is no payload, or when the payload carries no rightText — the string
   * is empty once nothing is left to report, and an empty button would render
   * as a bare chevron.
   *
   * @param {Object|null} payload - Normalized left-block payload
   * @private
   */
  _writeRemaining(payload) {
    if (!this.remainingElement) return;

    const text = payload?.rightText || '';
    if (this.remainingTextElement) this.remainingTextElement.textContent = text;
    this.remainingElement.hidden = !text;
  }

  /**
   * Write a linear progress bar into a host element, building the markup once
   * and then mutating the fill's width so the CSS transition animates.
   *
   * Serves the chat-panel progress strip. Kept generic — it makes no assumption
   * about its host beyond "an element to own the markup".
   *
   * @param {HTMLElement|null} host
   * @param {number|null} percent - null hides the host
   * @private
   */
  _writeLinearBar(host, percent) {
    if (!host) return;

    if (typeof percent !== 'number') {
      host.innerHTML = '';
      host.hidden = true;
      return;
    }

    const fill = host.querySelector('.header-linear-progress__fill');
    if (!fill) {
      host.innerHTML = this._buildLinearBar(percent);
    } else {
      fill.style.width = `${percent}%`;
      host.querySelector('.header-linear-progress')
        ?.setAttribute('aria-valuenow', String(Math.round(percent)));
    }
    host.hidden = false;
  }

  /**
   * Linear progress markup for the chat-panel strip.
   * @param {number} percent - 0-100
   * @returns {string} Progress bar HTML
   * @private
   */
  _buildLinearBar(percent) {
    const safe = Math.max(0, Math.min(100, Number(percent) || 0));
    return `<div class="header-linear-progress" role="progressbar" aria-valuenow="${Math.round(safe)}" aria-valuemin="0" aria-valuemax="100"><div class="header-linear-progress__fill" style="width:${safe}%"></div></div>`;
  }

  /**
   * Shallow-equal comparison for normalized left-block payloads.
   * @private
   */
  _leftBlockEqual(a, b) {
    if (a === b) return true;
    if (a === null || b === null) return false;
    return a.title === b.title
      && a.rightText === b.rightText
      && a.percent === b.percent;
  }

  // ===== MIDDLE BLOCK =====

  /**
   * Public API: set the header's middle row (the dynamic text element).
   *
   * Callers pass their descriptor markup only; single-line truncation is the
   * row's own CSS, so no wrapper element is expected or added.
   *
   * IMPORTANT: Only use with TRUSTED content (backend-generated or hardcoded).
   *
   * @param {{html?: string}|string|null} payload
   */
  setMiddleBlock(payload) {
    let html;
    if (payload == null) {
      html = '';
    } else if (typeof payload === 'string') {
      html = payload;
    } else {
      html = payload.html || '';
    }

    if (html === this._middleHtml) return;
    this._middleHtml = html;
    this._renderMiddle();
  }

  /** Clear the middle row. */
  clearMiddleBlock() {
    this.setMiddleBlock({ html: '' });
  }

  /**
   * Fade-swap the middle row's contents.
   *
   * Payloads are written as-is: #header-dynamic-text is a block container that
   * truncates its own inline content to one line (see .header__dynamic-text), so
   * no wrapper element is needed to carry the ellipsis.
   *
   * @private
   */
  _renderMiddle() {
    if (!this.dynamicTextElement) return;

    const html = this._middleHtml || '';
    if (this.dynamicTextElement.innerHTML === html) return;

    this.dynamicTextElement.classList.add('header__dynamic-text--fade');
    setTimeout(() => {
      this.dynamicTextElement.innerHTML = html;
      this.dynamicTextElement.classList.remove('header__dynamic-text--fade');
      // Reveal here, after the write — never at setMiddleBlock time: this
      // callback is only reached for genuinely changed content (the
      // `html === this._middleHtml` early return), and the row must not
      // expand while the DOM still holds the previous descriptor.
      if (html) this._revealMiddleRow();
    }, HeaderManager.FADE_MS);
  }

  /**
   * Scroll-intent consumer: collapse ('down') or restore ('up') the middle
   * row. Purely a class toggle — the collapse CSS lives behind the <760px
   * media query, so this is inert on desktop layouts.
   * @param {{direction: 'down'|'up'}} data
   */
  handleScrollIntent(data) {
    if (!this.middleSection) return;
    this.middleSection.classList.toggle('header__middle--hidden', data?.direction === 'down');
  }

  /**
   * Un-collapse the middle row (mode change, new descriptor, rotation).
   * @private
   */
  _revealMiddleRow() {
    this.middleSection?.classList.remove('header__middle--hidden');
  }

  /**
   * Layout-width changes invalidate a gesture-driven collapse: the class only
   * renders below 760px, so crossing that boundary and returning must not
   * resurrect a stale collapsed state.
   */
  handleViewportResize() {
    this._revealMiddleRow();
  }

  /**
   * Activate shimmer effect on dynamic text
   * Called when AI is thinking/processing
   */
  activateShimmer() {
    if (this.dynamicTextElement) {
      this.dynamicTextElement.classList.add('header__dynamic-text--shimmer');
    }
  }

  /**
   * Deactivate shimmer effect on dynamic text
   * Called when AI processing completes
   */
  deactivateShimmer() {
    if (this.dynamicTextElement) {
      this.dynamicTextElement.classList.remove('header__dynamic-text--shimmer');
    }
  }

  /**
   * Initialize tooltip behavior for header icon buttons
   */
  initializeTooltips() {
    Tooltip.setupAll(this.headerElement, '[data-tooltip]', {
      position: 'below'
    });
  }
}
